import { useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  rollForward,
  toKr,
  type Classification,
  type LedgerEvent,
} from '@heivol-finance/calc';
import { netWorthApi, budgetApi, type NetWorthData, type NetWorthSettings } from '../lib/data.js';
import { formatOre } from '../lib/format.js';
import { chartColors } from '../lib/chart.js';
import { PageHeader, Card, StatCard, DeleteButton, TextField, OreField, NumberField, Select } from '../components/ui.js';
import { SectionBar, Tr, Th, Td, TdNum } from '../components/table.js';

const CLASS_OPTS: { value: Classification; label: string }[] = [
  { value: 'consumptive', label: 'Consumptive (−)' },
  { value: 'asset-converting', label: 'Asset (neutral)' },
  { value: 'debt-service', label: 'Debt repayment (neutral)' },
];
const classLabel = (c: Classification) => CLASS_OPTS.find((o) => o.value === c)?.label ?? c;

/** Month index of a YYYY-MM-DD date relative to the projection start (startYear/startMonth). */
function monthIndex(dated: string, startYear: number, startMonth: number): number {
  const parts = dated.split('-');
  const y = Number(parts[0] ?? startYear);
  const m = Number(parts[1] ?? 1);
  return (y - startYear) * 12 + (m - startMonth);
}
function monthLabel(startYear: number, startMonth: number, i: number): string {
  const total = (startYear * 12 + (startMonth - 1)) + i;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return `${String(m).padStart(2, '0')}/${y}`;
}

export function NetWorth(): JSX.Element {
  const [data, setData] = useState<NetWorthData | null>(null);
  const [baselineOre, setBaselineOre] = useState(0);

  const reload = () => netWorthApi.load().then(setData);
  useEffect(() => {
    void reload();
    void budgetApi.load().then((b) => setBaselineOre(b.summary.netWorthBaseline));
  }, []);

  const colors = useMemo(() => chartColors(), [data]);

  const model = useMemo(() => {
    if (!data) return null;
    const st = data.settings;
    const events: LedgerEvent[] = data.events.map((e) => ({
      month: monthIndex(e.dated, st.startYear, st.startMonth),
      label: e.label,
      amount: e.amount_ore,
      classification: e.classification,
    }));
    const points = rollForward({
      openingNetWorth: st.openingNetWorthOre,
      months: st.horizonMonths,
      monthlyBudgetNet: baselineOre,
      events: events.filter((e) => e.month >= 0 && e.month < st.horizonMonths),
    });
    const eventMonths = new Set(events.map((e) => e.month));
    const series = points.map((p) => ({
      i: p.month,
      date: monthLabel(st.startYear, st.startMonth, p.month),
      accumulated: toKr(p.accumulated),
      eventDot: eventMonths.has(p.month) ? toKr(p.accumulated) : null,
    }));
    const end = points.at(-1)?.accumulated ?? st.openingNetWorthOre;
    const consumptiveTotal = data.events
      .filter((e) => e.classification === 'consumptive')
      .reduce((s, e) => s + e.amount_ore, 0);
    return { series, end, consumptiveTotal, settings: st };
  }, [data, baselineOre]);

  if (!data || !model) return <PageHeader title="Net Worth" subtitle="Loading…" />;

  const st = model.settings;
  const patchSettings = (patch: Partial<NetWorthSettings>) =>
    netWorthApi.saveSettings({ ...st, ...patch }).then(reload);

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Net Worth"
        subtitle={
          <>
            Your accumulated net worth month by month — based on the budget plus one-off events.
            Reproduces the spreadsheet's <span className="font-mono">Accumulated</span> tab.
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Net worth now (opening)" value={formatOre(st.openingNetWorthOre)} />
        <StatCard label="Baseline / mo." value={formatOre(baselineOre)} hint="From the budget" tone="ai" />
        <StatCard
          label={`Net worth in ${Math.round(st.horizonMonths / 12)} years`}
          value={formatOre(model.end)}
          tone="success"
        />
        <StatCard label="Consumed (one-off)" value={formatOre(model.consumptiveTotal)} tone="warning" />
      </div>

      <Card className="mb-6 p-4">
        <div className="mb-2 text-hv-sm font-medium text-hv-text">Net-worth curve</div>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={model.series} margin={{ top: 8, right: 12, bottom: 4, left: 8 }}>
              <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                stroke={colors.axis}
                tick={{ fontSize: 11 }}
                interval={Math.max(0, Math.floor(model.series.length / 8) - 1)}
              />
              <YAxis
                stroke={colors.axis}
                tick={{ fontSize: 11 }}
                width={70}
                tickFormatter={(v: number) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
              />
              <Tooltip
                formatter={(v: number) => formatOre(Math.round(v * 100))}
                contentStyle={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text }}
                labelStyle={{ color: colors.text }}
              />
              <Line type="monotone" dataKey="accumulated" name="Net worth" stroke={colors.ai} strokeWidth={2} dot={false} />
              <Line
                type="monotone"
                dataKey="eventDot"
                name="One-off event"
                stroke="transparent"
                dot={{ r: 4, fill: colors.chat, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Settings */}
      <Card className="mb-6 p-4">
        <div className="mb-3 text-hv-sm font-medium text-hv-text">Assumptions</div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Labeled label="Opening net worth">
            <OreField value={st.openingNetWorthOre} onCommit={(v) => patchSettings({ openingNetWorthOre: v })} />
          </Labeled>
          <Labeled label="Horizon (months)">
            <NumberField value={st.horizonMonths} onCommit={(v) => patchSettings({ horizonMonths: Math.round(v) })} />
          </Labeled>
          <Labeled label="Start year">
            <NumberField value={st.startYear} onCommit={(v) => patchSettings({ startYear: Math.round(v) })} />
          </Labeled>
          <Labeled label="Start month">
            <NumberField value={st.startMonth} onCommit={(v) => patchSettings({ startMonth: Math.round(v) })} />
          </Labeled>
        </div>
      </Card>

      {/* Ledger */}
      <Card className="overflow-hidden">
        <SectionBar
          title="One-off events"
          onAdd={() =>
            netWorthApi
              .addEvent({ dated: `${st.startYear}-01-01`, label: 'New entry', amount_ore: 0, classification: 'consumptive' })
              .then(reload)
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-hv-sm">
            <thead>
              <Tr head>
                <Th>Date</Th>
                <Th>Entry</Th>
                <Th right>Amount</Th>
                <Th>Classification</Th>
                <Th right>Net-worth effect</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {data.events.map((e) => (
                <Tr key={e.id}>
                  <Td>
                    <TextField value={e.dated} onCommit={(v) => netWorthApi.updateEvent(e.id, { dated: v }).then(reload)} />
                  </Td>
                  <Td>
                    <TextField value={e.label} onCommit={(v) => netWorthApi.updateEvent(e.id, { label: v }).then(reload)} />
                  </Td>
                  <Td right>
                    <OreField value={e.amount_ore} onCommit={(v) => netWorthApi.updateEvent(e.id, { amount_ore: v }).then(reload)} />
                  </Td>
                  <Td>
                    <Select
                      value={e.classification}
                      options={CLASS_OPTS}
                      onChange={(v) => netWorthApi.updateEvent(e.id, { classification: v }).then(reload)}
                    />
                  </Td>
                  <TdNum>
                    <span className={e.classification === 'consumptive' ? 'text-hv-warning-text' : 'text-hv-text-tertiary'}>
                      {e.classification === 'consumptive' ? `−${formatOre(e.amount_ore)}` : formatOre(0)}
                    </span>
                  </TdNum>
                  <Td>
                    <DeleteButton onClick={() => netWorthApi.deleteEvent(e.id).then(reload)} />
                  </Td>
                </Tr>
              ))}
              {data.events.length === 0 && (
                <Tr>
                  <Td colSpan={6}>
                    <span className="text-hv-text-tertiary">No one-off events — the curve follows the budget baseline.</span>
                  </Td>
                </Tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-hv-border px-4 py-2 text-hv-xs text-hv-text-tertiary">
          Consumptive entries are subtracted from net worth. Asset conversion (e.g. house, car) and debt repayment are net-worth-neutral — see {classLabel('asset-converting')}.
        </div>
      </Card>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-hv-xs uppercase tracking-wide text-hv-text-tertiary">{label}</span>
      {children}
    </label>
  );
}
